import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, BookOpen, FileText, Download, ArrowRight } from 'lucide-react';
import { educationResources, faqItems } from '../data/mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const EducationPage = () => {
  return (
    <main>
      <div className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={16} />
            <span>Education & Resources</span>
          </div>
          <h1 className="display-medium">Education & Resources</h1>
          <p className="body-large">
            Promoting responsible equine ownership through information and guidance.
          </p>
        </div>
      </div>

      {/* Introduction */}
      <section className="section">
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>Knowledge for Better Horse Care</h2>
            <p className="body-medium" style={{ marginBottom: '16px' }}>
              One of our charitable purposes is to advance public education on responsible equine ownership and welfare. We believe that informed owners make better decisions for their horses.
            </p>
            <p className="body-medium">
              Below you’ll find guides, factsheets, and answers to common questions about horse welfare and ownership.
            </p>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Guides & Resources</h2>
            <p className="body-medium">Practical information for current and prospective horse owners.</p>
          </div>
          <div className="grid-2">
            {educationResources.map((resource) => (
              <div key={resource.id} className="network-card hover-lift">
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div 
                    style={{ 
                      width: '48px', 
                      height: '48px', 
                      background: 'var(--brand-primary)', 
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    {resource.type === 'Guide' ? (
                      <BookOpen size={24} color="var(--brand-dark)" />
                    ) : (
                      <FileText size={24} color="var(--brand-dark)" />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <span 
                      className="body-small" 
                      style={{ 
                        background: 'var(--bg-subtle)', 
                        padding: '4px 10px', 
                        borderRadius: '8px',
                        fontWeight: 500,
                        display: 'inline-block',
                        marginBottom: '8px'
                      }}
                    >
                      {resource.type}
                    </span>
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>{resource.title}</h3>
                    <p className="body-medium" style={{ marginBottom: '16px' }}>{resource.description}</p>
                    <button 
                      className="btn-secondary"
                      style={{ padding: '8px 16px', fontSize: '14px', minHeight: 'auto' }}
                    >
                      <Download size={16} style={{ marginRight: '6px' }} />
                      Coming Soon
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Reasons */}
      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center' }}>
            <div>
              <h2 className="heading-1" style={{ marginBottom: '24px' }}>Why Horses Need New Homes</h2>
              <p className="body-medium" style={{ marginBottom: '24px' }}>
                Understanding why horses end up needing new homes helps us all work towards prevention. Common circumstances include:
              </p>
              <ul style={{ paddingLeft: '20px' }}>
                {[
                  'Financial difficulties or job loss',
                  'Owner illness or disability',
                  'Bereavement or family breakdown',
                  'Moving to unsuitable accommodation',
                  'Horse behavioural or health issues',
                  'Lack of knowledge about care requirements',
                  'Overbreeding and impulse purchases'
                ].map((item, index) => (
                  <li key={index} className="body-medium" style={{ marginBottom: '12px' }}>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="body-medium" style={{ marginTop: '24px' }}>
                There is no shame in asking for help. What matters is doing the right thing for the horse.
              </p>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1743787505796-9b2fdffba1a2?w=800&auto=format&fit=crop"
                alt="Horse being cared for"
                style={{ width: '100%', borderRadius: '24px', boxShadow: '0 8px 32px rgba(0,69,52,0.15)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Frequently Asked Questions</h2>
            <p className="body-medium">Common questions about horse welfare and The Grazing Trust.</p>
          </div>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Accordion type="single" collapsible>
              {faqItems.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} style={{ marginBottom: '12px' }}>
                  <AccordionTrigger 
                    style={{ 
                      background: 'white',
                      padding: '20px 24px',
                      borderRadius: '16px',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-medium)'
                    }}
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent 
                    style={{ 
                      padding: '16px 24px',
                      background: 'white',
                      borderRadius: '0 0 16px 16px',
                      marginTop: '-8px',
                      borderLeft: '1px solid var(--border-medium)',
                      borderRight: '1px solid var(--border-medium)',
                      borderBottom: '1px solid var(--border-medium)'
                    }}
                  >
                    <p className="body-medium">{faq.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2 className="display-small">Need More Information?</h2>
          <p className="body-large" style={{ maxWidth: '600px', margin: '0 auto 32px' }}>
            We’re here to help with questions about horse welfare and responsible ownership.
          </p>
          <Link to="/contact" className="btn-cta">
            Contact Us
            <ArrowRight size={20} style={{ marginLeft: '8px' }} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default EducationPage;
